<template>
  <main class="w-full max-w-none">
    <div class="mx-auto w-full max-w-full px-0">
      <div class="mb-8">
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">三方支付制单</h1>
        <AdvancedFeatureNotice api-url="/api/v2/payment/thirdPay" />
        <p class="text-gray-600">
          提交三方支付指令（POST /api/v2/payment/thirdPay）。支付状态请使用「查询支付状态」接口。
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-5 gap-3 w-full">
        <!-- 左侧：接口文档 -->
        <div class="w-full lg:col-span-3">
          <Card class="mb-8">
            <CardHeader>
              <CardTitle>接口说明</CardTitle>
              <CardDescription>
                三方支付制单接口，用于提交三方支付订单（如 Paypal 等，请求体为嵌套 JSON，与对外 API 文档一致）。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div class="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <span class="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">POST</span>
                <code class="text-sm font-mono">/api/v2/payment/thirdPay</code>
              </div>

              <div class="space-y-6">
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">
                    请求头参数
                  </h3>
                  <div class="overflow-x-auto">
                    <table class="table-fixed w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            参数名
                          </th>
                          <th class="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            类型
                          </th>
                          <th class="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            必需
                          </th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            描述
                          </th>
                          <th class="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            示例值
                          </th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr>
                          <td class="px-4 py-3 text-sm font-medium text-gray-900 break-all">
                            x-app-id
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            String
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            是
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            APP ID
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500 break-all">
                            4d9414e89bc24b0d89b678d9f20bc56a
                          </td>
                        </tr>
                        <tr>
                          <td class="px-4 py-3 text-sm font-medium text-gray-900 break-all">
                            x-app-secret
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            String
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            是
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            API Key / AppSecret
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500 break-all">
                            8201e508ef948b8fcdca855729e7ee34
                          </td>
                        </tr>
                        <tr>
                          <td class="px-4 py-3 text-sm font-medium text-gray-900 break-all">
                            x-customer-code
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            String
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            是
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            客户编号
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500 break-all">
                            CUSTFA48DD8EFF437956
                          </td>
                        </tr>
                        <tr>
                          <td class="px-4 py-3 text-sm font-medium text-gray-900 break-all">
                            Content-Type
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            String
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            是
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            发送内容类型
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500 break-all">
                            application/json
                          </td>
                        </tr>
                        <tr>
                          <td class="px-4 py-3 text-sm font-medium text-gray-900 break-all">
                            xencio-key
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            String
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            否
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            加密密钥，当启用国密加密的时候该请求头必传
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500 break-all">
                            BM/UBCOn2yrZINuOdvaS01KjftyyRagN/LU2zVntwD8svrIObu/2Sqjl041C3RyqsUlE02ERmRBNgoPcoyhM7Tm1Quv55QcQ9hDNX0GO3997OANU7HQGHYlJ2aDEf0pTGNC0Lu5CzAwSM76dTorLyGI=
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- 请求参数（分组） -->
                <div v-for="group in requestParamGroups" :key="group.title">
                  <h3 class="text-lg font-medium text-gray-900 mb-3">{{ group.title }}</h3>
                  <div class="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table class="table-fixed w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50 sticky top-0">
                        <tr>
                          <th class="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">参数名</th>
                          <th class="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                          <th class="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">必需</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                          <th class="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">示例值</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="param in group.params" :key="param.name">
                          <td class="px-4 py-3 text-sm font-medium text-gray-900 break-all">{{ param.name }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ param.type }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            <span :class="param.required === '是' ? 'font-medium' : ''">{{ param.required }}</span>
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ param.description }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500 break-all">{{ param.example || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- 响应参数 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">响应参数</h3>
                  <div class="overflow-x-auto">
                    <table class="table-fixed w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">字段</th>
                          <th class="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">说明</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="r in responseParams" :key="r.field">
                          <td class="px-4 py-3 text-sm font-medium text-gray-900 break-all">{{ r.field }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ r.type }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ r.description }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- 请求示例 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">请求示例</h3>
                  <pre class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"><code class="language-http">POST /api/v2/payment/thirdPay
Content-Type: application/json

{
  "businessTransNo": "082615592926532",
  "bizScene": "",
  "payAmount": "100.00",
  "payCurrency": "USD",
  "purpose": "GOODS",
  "postscript": "XX附言",
  "summary": "YY摘要",
  "fromAccountInfo": {
    "fromAccountNo": "21202200214xxxxx",
    "fromAccountName": "test_bind_masteraccount",
    "fromAccountCurrency": "USD"
  },
  "toAccountInfo": {
    "toAccountNo": "21202200214xxxxx",
    "toAccountName": "test_bind_subaccount",
    "toAccountCurrency": "USD",
    "toAccountType": "email"
  },
  "metaJson": "{\"projectInfo\":\"测试项目信息\"}"
}</code></pre>
                </div>

                <!-- 成功响应示例 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">成功响应示例</h3>
                  <pre class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"><code class="language-json">{
  "status": "OK",
  "code": null,
  "message": "付款指令已提交，后续请根据支付状态查询接口查询支付结果",
  "data": {
    "businessTransNo": "082615592926532",
    "paySerialNo": "YQT-000000001"
  }
}</code></pre>
                </div>

                <!-- 失败响应示例 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">失败响应示例</h3>
                  <pre class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"><code class="language-json">{
  "status": "OK",
  "code": "400",
  "message": "付款金额必须大于0",
  "data": null
}</code></pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- 右侧"试一试"区域 -->
        <div class="w-full lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>试一试</CardTitle>
              <CardDescription>使用不同语言测试API</CardDescription>
            </CardHeader>
            <CardContent>
              <div class="border-b border-gray-200">
                <nav class="-mb-px flex space-x-8 overflow-x-auto">
                  <button
                    v-for="language in languages"
                    :key="language.id"
                    @click="activeLanguage = language.id"
                    class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                    :class="[
                      activeLanguage === language.id
                        ? 'border-[var(--primary)] text-[var(--primary)]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    ]"
                  >
                    {{ language.name }}
                  </button>
                </nav>
              </div>

              <div class="mt-6">
                <!-- 国密加密开关 -->
                <label class="inline-flex items-center gap-2 mb-3 select-none">
                  <input
                    type="checkbox"
                    v-model="enableGm"
                    class="rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]"
                  />
                  <span
                    class="text-sm"
                    :class="enableGm ? 'text-[var(--primary)] font-medium' : 'text-gray-700'"
                    >启用国密加密（SM2/SM3/SM4）</span
                  >
                  <button
                    type="button"
                    class="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold hover:bg-blue-200"
                    @click="showGmHelp = true"
                    aria-label="查看国密加密传输说明"
                  >
                    ?
                  </button>
                </label>
                <div ref="trySampleCodeRoot">
                  <pre
                    v-if="!enableGm"
                    class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"
                  ><code :key="`plain-${activeLanguage}`">{{ getRequestText() }}</code></pre>
                  <pre
                    v-if="enableGm"
                    class="bg-gray-900 text-gray-100 p-4 rounded-md text-sm overflow-x-auto mt-3"
                  ><code :key="`gm-${activeLanguage}`">{{ getGmSampleText() }}</code></pre>
                </div>
                <!-- 国密说明弹框 -->
                <div v-if="showGmHelp" class="fixed inset-0 z-50">
                  <div
                    class="absolute inset-0 bg-black/40"
                    @click="showGmHelp = false"
                  ></div>
                  <div
                    class="absolute inset-0 flex items-center justify-center p-4"
                  >
                    <div
                      class="w-full max-w-[90vw] md:max-w-2xl rounded-lg bg-white shadow-xl max-h-[85vh] flex flex-col"
                    >
                      <div
                        class="px-4 py-3 border-b border-gray-200 flex items-center justify-end"
                      >
                        <h3 class="sr-only">国密加密传输说明</h3>
                        <button
                          type="button"
                          class="p-1 rounded hover:bg-gray-100"
                          @click="showGmHelp = false"
                        >
                          ✕
                        </button>
                      </div>
                      <div class="px-4 py-4 overflow-y-auto">
                        <GmHelpContent />
                      </div>
                      <div
                        class="px-4 py-3 border-t border-gray-200 flex justify-end"
                      >
                        <button
                          type="button"
                          class="px-3 py-1.5 rounded bg-[var(--primary)] text-white hover:opacity-90"
                          @click="showGmHelp = false"
                        >
                          关闭
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 国密加密开关与密钥输入 -->
                <div
                  v-if="enableGm"
                  class="pt-2 border-t border-gray-200"
                  style="margin-bottom: 0.5rem"
                >
                  <div
                    class="mt-4 rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <div class="flex items-center justify-between px-3 py-2">
                      <div class="text-sm font-medium text-gray-800">
                        国密加密传输（SM2/SM3/SM4）
                      </div>
                      <span
                        v-if="enableGm"
                        class="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700"
                        >已启用</span
                      >
                    </div>
                    <div class="flex items-center justify-between px-3 py-2">
                      <a
                        :href="getBaseDomain() + '/#/app-params'"
                        target="_blank"
                        class="card-link"
                        style="
                          color: var(--primary);
                          font-weight: 500;
                          font-style: italic;
                        "
                      >
                        <KeyOutlined class="w-4 h-4" />
                        获取/配置密钥
                      </a>
                    </div>
                    <div class="px-3 pb-3">
                      <div class="grid grid-cols-1 gap-3">
                        <div>
                          <label
                            class="block text-sm font-medium text-gray-700 mb-1"
                            >平台公钥（SM2 公钥，Base64）</label
                          >
                          <textarea
                            v-model="xencioPublicKey"
                            rows="3"
                            class="w-full px-3 py-2 font-mono tracking-wider border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)] resize-y"
                            placeholder="粘贴见知提供的 SM2 公钥（Base64，标准编码）"
                          ></textarea>
                          <div class="mt-1 flex items-center justify-between">
                            <p class="text-xs text-gray-500">
                              用于加密
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">reqData</code>、<code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">sign</code>
                              以及 Header
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">xencio-key</code>。
                            </p>
                            <div class="flex items-center gap-2">
                              <span
                                v-if="
                                  xencioPublicKey && isBase64(xencioPublicKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                >Base64格式正确</span
                              >
                              <span
                                v-else-if="
                                  xencioPublicKey && !isBase64(xencioPublicKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                                >Base64格式错误</span
                              >
                              <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-700"
                                @click="xencioPublicKey = ''"
                              >
                                清空
                              </button>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label
                            class="block text-sm font-medium text-gray-700 mb-1"
                            >企业 SM2 私钥（Base64）</label
                          >
                          <textarea
                            v-model="sm2PrivateKey"
                            rows="3"
                            class="w-full px-3 py-2 font-mono tracking-wider border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)] resize-y"
                            placeholder="粘贴企业自有 SM2 私钥（Base64，标准编码）"
                          ></textarea>
                          <div class="mt-1 flex items-center justify-between">
                            <p class="text-xs text-gray-500">
                              用于解密响应
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">respData</code>、<code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">sign</code>
                              以及 Header
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">xencio-key</code>。
                            </p>
                            <div class="flex items-center gap-2">
                              <span
                                v-if="sm2PrivateKey && isBase64(sm2PrivateKey)"
                                class="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                >Base64格式正确</span
                              >
                              <span
                                v-else-if="
                                  sm2PrivateKey && !isBase64(sm2PrivateKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                                >Base64格式错误</span
                              >
                              <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-700"
                                @click="sm2PrivateKey = ''"
                              >
                                清空
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="space-y-4 mt-6">
                  <!-- Header 参数 -->
                  <div class="border-b border-gray-200 pb-4 mb-2">
                    <h4 class="text-sm font-medium text-gray-900 mb-3">请求头参数</h4>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">x-app-id <span class="text-red-500">*</span></label>
                      <input v-model="appId" type="text" :class="{ '!border-red-500': showValidation && !appId }" placeholder="请输入 x-app-id"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]" />
                    </div>
                    <div class="mt-3">
                      <label class="block text-sm font-medium text-gray-700 mb-1">x-app-secret <span class="text-red-500">*</span></label>
                      <input v-model="appSecret" type="text"  :class="{ '!border-red-500': showValidation && !appSecret }" placeholder="请输入 x-app-secret"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]" />
                    </div>
                    <div class="mt-3">
                      <label class="block text-sm font-medium text-gray-700 mb-1">x-customer-code <span class="text-red-500">*</span></label>
                      <input v-model="customerCode" type="text" :class="{ '!border-red-500': showValidation && !customerCode }" placeholder="请输入 x-customer-code"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]" />
                    </div>
                  </div>

                  <!-- 业务参数 -->
                  <div v-for="group in formGroups" :key="group.title">
                    <h4 class="text-sm font-semibold mb-2 text-gray-700">{{ group.title }}</h4>
                    <div class="mb-3 rounded-lg">
                      <div class="grid grid-cols-1 gap-3">
                        <div v-for="field in group.fields" :key="joinFieldPath(group.pathPrefix, field.path)">
                          <label class="block text-sm font-medium text-gray-700 mb-1">
                            {{ field.path }}
                            <span v-if="field.required" class="text-red-500">*</span>
                          </label>
                          <select
                            v-if="field.control === 'select'"
                            :value="fieldGet(joinFieldPath(group.pathPrefix, field.path))"
                            @change="fieldSet(joinFieldPath(group.pathPrefix, field.path), ($event.target as HTMLSelectElement).value)"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                          >
                            <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                          </select>
                          <input
                            v-else
                            :value="fieldGet(joinFieldPath(group.pathPrefix, field.path))"
                            type="text"
                            :placeholder="field.placeholder"
                            @input="fieldSet(joinFieldPath(group.pathPrefix, field.path), ($event.target as HTMLInputElement).value)"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                          />
                          <p v-if="field.hint" class="text-xs text-gray-500 mt-1">{{ field.hint }}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="mb-3 rounded-lg">
                    <label class="block text-sm font-medium text-gray-700">metaJson</label>
                    <textarea
                      v-model="metaJson"
                      rows="3"
                      placeholder='{"projectInfo":"测试项目信息"}'
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                    ></textarea>
                    <p class="text-xs text-gray-500 mt-1">留空则不传 metaJson；须为 JSON 字符串。</p>
                  </div>

                  <button
                    @click="submitPayment"
                    :disabled="loading"
                    class="w-full bg-[var(--primary)] hover:bg-[color:var(--primary)] text-white font-medium py-2 px-4 rounded-md transition duration-200 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ loading ? "提交中..." : "发起三方支付制单请求" }}
                  </button>
                </div>
              </div>

              <!-- 响应结果 -->
              <div v-if="response" ref="tryResponseCodeRoot" class="mt-6">
                <h3 class="text-lg font-medium text-gray-900 mb-2">响应结果</h3>
                <pre
                  class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"
                ><code>{{ response }}</code></pre>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import GmHelpContent from "./ui/GmHelpContent.vue";
import { ref, onMounted, watch, nextTick } from "vue";
import Card from "./ui/Card.vue";
import CardHeader from "./ui/CardHeader.vue";
import CardTitle from "./ui/CardTitle.vue";
import CardDescription from "./ui/CardDescription.vue";
import CardContent from "./ui/CardContent.vue";
import AdvancedFeatureNotice from "./ui/AdvancedFeatureNotice.vue";
import httpService from "@/lib/http";
import hljs from "highlight.js/lib/core";
import { useSystemStore } from "@/stores/system";
import { getBaseDomain } from "@/lib/config";
import * as sm from "sm-crypto";
import { get as lodashGet, set as lodashSet } from "lodash";

const serverUrl = import.meta.env.VITE_API_URL;
const loading = ref(false);
const response = ref("");
const activeLanguage = ref("curl");

// ── 请求参数分组（文档展示，与对外 API 一致） ──
const requestParamGroups = [
  {
    title: "根级字段",
    params: [
      { name: "businessTransNo", type: "String", required: "是", description: "外部交易流水号。业务唯一编号，16位长度，不可重复；指令序号和电子凭证号不可同时为空", example: "082615592926532" },
      { name: "bizScene", type: "String", required: "条件", description: "业务场景", example: "" },
      { name: "payAmount", type: "String", required: "是", description: "付款金额。单位（元），保留两位小数", example: "100.00" },
      { name: "payCurrency", type: "String", required: "是", description: "付款币别", example: "USD" },
      { name: "purpose", type: "String", required: "否", description: '用途。Paypal 时非必填，取值范围: "AWARDS" | "PRIZES" | "DONATIONS" | "GOODS" | "SERVICES" | "REBATES" | "CASHBACK" | "DISCOUNTS" | "NON_GOODS_OR_SERVICES"', example: "GOODS" },
      { name: "postscript", type: "String", required: "条件", description: "附言", example: "XX附言" },
      { name: "summary", type: "String", required: "条件", description: "摘要", example: "YY摘要" },
      { name: "metaJson", type: "String", required: "否", description: "扩展信息域。JSON 字符串", example: '{"projectInfo":"测试项目信息"}' },
    ],
  },
  {
    title: "fromAccountInfo",
    params: [
      { name: "fromAccountNo", type: "String", required: "是", description: "付款账号", example: "21202200214xxxxx" },
      { name: "fromAccountName", type: "String", required: "否", description: "付款账户名称", example: "test_bind_masteraccount" },
      { name: "fromAccountCurrency", type: "String", required: "条件", description: "付款账户币别", example: "USD" },
    ],
  },
  {
    title: "toAccountInfo",
    params: [
      { name: "toAccountNo", type: "String", required: "是", description: "收款账号", example: "21202200214xxxxx" },
      { name: "toAccountName", type: "String", required: "条件", description: "收款账户名称", example: "test_bind_subaccount" },
      { name: "toAccountCurrency", type: "String", required: "条件", description: "收款账户币别", example: "USD" },
      { name: "toAccountType", type: "String", required: "条件", description: "收款账户类型。使用 Paypal 付款时必填；字典：email-邮箱，venmo-Venmo账户", example: "email" },
    ],
  },
];

const responseParams = [
  { field: "code", type: "String", description: "返回码" },
  { field: "message", type: "String", description: "返回信息" },
  { field: "data.businessTransNo", type: "String", description: "外部交易流水号" },
  { field: "data.paySerialNo", type: "String", description: "内部交易流水号（支付平台流水号）" },
  { field: "realJsonModel", type: "String", description: "实际请求报文JSON" },
  { field: "status", type: "String", description: "状态" },
  { field: "successResponse", type: "Boolean", description: "是否成功响应" },
];

// 「试一试」内动态代码块（避免误处理左侧文档里带 language-* 的静态示例）
const trySampleCodeRoot = ref<HTMLElement | null>(null);
const tryResponseCodeRoot = ref<HTMLElement | null>(null);

// ── 代码高亮（与 paymentSubmitPage 一致：随语言 / 国密开关刷新） ──
const highlightCode = () => {
  nextTick(() => {
    setTimeout(() => {
      const roots = [trySampleCodeRoot.value, tryResponseCodeRoot.value].filter(
        Boolean
      ) as HTMLElement[];
      const blocks: HTMLElement[] = [];
      roots.forEach((root) => {
        root.querySelectorAll("pre code").forEach((el) => {
          blocks.push(el as HTMLElement);
        });
      });
      if (!blocks.length) return;

      const languageMap: Record<string, string> = {
        curl: "language-bash",
        javascript: "language-javascript",
        python: "language-python",
        java: "language-java",
        csharp: "language-csharp",
        php: "language-php",
        ruby: "language-ruby",
      };
      const langClass = languageMap[activeLanguage.value];
      blocks.forEach((block) => {
        block.className = "";
        block.removeAttribute("data-highlighted");
        block.classList.add("hljs");
        if (langClass) {
          block.classList.add(langClass);
        }
        try {
          hljs.highlightElement(block);
        } catch (error) {
          console.warn("代码高亮失败:", error);
        }
      });
    }, 100);
  });
};

highlightCode();

// 系统配置
const systemStore = useSystemStore();

// 默认值
const DEFAULT_APP_ID = "登录后自动获取";
const DEFAULT_APP_SECRET = "登录后自动获取";
const DEFAULT_CUSTOMER_CODE = "登录后自动获取";
const DEFAULT_XENCIO_PUBLIC_KEY = "登录后自动获取";

const appId = ref(DEFAULT_APP_ID);
const appSecret = ref(DEFAULT_APP_SECRET);
const customerCode = ref(DEFAULT_CUSTOMER_CODE);
const xencioPublicKey = ref(DEFAULT_XENCIO_PUBLIC_KEY);
const showValidation = ref(false);
const metaJson = ref("");

type ThirdPartyPayFormState = {
  businessTransNo: string;
  bizScene: string;
  payAmount: string;
  payCurrency: string;
  purpose: string;
  postscript: string;
  summary: string;
  fromAccountInfo: {
    fromAccountNo: string;
    fromAccountName: string;
    fromAccountCurrency: string;
  };
  toAccountInfo: {
    toAccountNo: string;
    toAccountName: string;
    toAccountCurrency: string;
    toAccountType: string;
  };
};

const initialFormState = (): ThirdPartyPayFormState => ({
  businessTransNo: "",
  bizScene: "",
  payAmount: "",
  payCurrency: "",
  purpose: "",
  postscript: "",
  summary: "",
  fromAccountInfo: {
    fromAccountNo: "",
    fromAccountName: "",
    fromAccountCurrency: "",
  },
  toAccountInfo: {
    toAccountNo: "",
    toAccountName: "",
    toAccountCurrency: "",
    toAccountType: "",
  },
});

/**
 * 仅产出 API 约定的嵌套结构。优先读嵌套路径；若存在写在根上的同名片段，则并入嵌套。
 */
const normalizeThirdPartyPayFormState = (
  raw: Record<string, unknown>
): ThirdPartyPayFormState => {
  const r = (nestedPath: string, flatKey?: string): string => {
    let v = lodashGet(raw, nestedPath);
    if (v !== undefined && v !== null && String(v).trim() !== "")
      return String(v);
    if (flatKey !== undefined) {
      v = lodashGet(raw, flatKey);
      if (v !== undefined && v !== null) return String(v);
    }
    return "";
  };
  return {
    businessTransNo: r("businessTransNo"),
    bizScene: r("bizScene"),
    payAmount: r("payAmount"),
    payCurrency: r("payCurrency"),
    purpose: r("purpose"),
    postscript: r("postscript"),
    summary: r("summary"),
    fromAccountInfo: {
      fromAccountNo: r("fromAccountInfo.fromAccountNo", "fromAccountNo"),
      fromAccountName: r("fromAccountInfo.fromAccountName", "fromAccountName"),
      fromAccountCurrency: r(
        "fromAccountInfo.fromAccountCurrency",
        "fromAccountCurrency"
      ),
    },
    toAccountInfo: {
      toAccountNo: r("toAccountInfo.toAccountNo", "toAccountNo"),
      toAccountName: r("toAccountInfo.toAccountName", "toAccountName"),
      toAccountCurrency: r(
        "toAccountInfo.toAccountCurrency",
        "toAccountCurrency"
      ),
      toAccountType:
        r("toAccountInfo.toAccountType", "toAccountType"),
    },
  };
};

const formState = ref<ThirdPartyPayFormState>(initialFormState());

const fieldGet = (path: string) => {
  const v = lodashGet(formState.value, path);
  if (v === undefined || v === null) return "";
  return String(v);
};

const fieldSet = (path: string, val: string) => {
  lodashSet(formState.value as unknown as Record<string, unknown>, path, val);
};

type ThirdPartySubmitFormField = {
  path: string;
  required: boolean;
  placeholder: string;
  conditional?: boolean;
  hint?: string;
  control?: "input" | "select";
  options?: { value: string; label: string }[];
};

/** 分组内 path 为短名；与 pathPrefix 拼成 lodash 路径 */
const joinFieldPath = (pathPrefix: string | undefined, path: string) => {
  const p = (pathPrefix ?? "").trim();
  return p ? `${p}.${path}` : path;
};

type ThirdPartySubmitFormGroup = {
  title: string;
  pathPrefix?: string;
  required: boolean;
  fields: ThirdPartySubmitFormField[];
};

const formGroups: ThirdPartySubmitFormGroup[] = [
  {
    title: "根级字段",
    required: true,
    fields: [
      { path: "businessTransNo", required: true, placeholder: "外部交易流水号，16位，业务唯一" },
      { path: "bizScene", required: false, conditional: true, placeholder: "业务场景" },
      { path: "payAmount", required: true, placeholder: "付款金额，保留两位小数" },
      { path: "payCurrency", required: true, placeholder: "付款币别，如 USD" },
      { path: "purpose", required: false, placeholder: "用途" },
      { path: "postscript", required: false, conditional: true, placeholder: "附言" },
      { path: "summary", required: false, conditional: true, placeholder: "摘要" },
    ],
  },
  {
    title: "fromAccountInfo",
    pathPrefix: "fromAccountInfo",
    required: true,
    fields: [
      { path: "fromAccountNo", required: true, placeholder: "付款账号" },
      { path: "fromAccountName", required: false, placeholder: "付款账户名称" },
      { path: "fromAccountCurrency", required: false, conditional: true, placeholder: "付款账户币别，如 USD" },
    ],
  },
  {
    title: "toAccountInfo",
    pathPrefix: "toAccountInfo",
    required: true,
    fields: [
      { path: "toAccountNo", required: true, placeholder: "收款账号" },
      { path: "toAccountName", required: false, conditional: true, placeholder: "收款账户名称" },
      { path: "toAccountCurrency", required: false, conditional: true, placeholder: "收款账户币别，如 USD" },
      { path: "toAccountType", required: false, conditional: true, placeholder: "收款账户类型", hint: "使用 Paypal 付款时必填" },
    ],
  },
];

// 国密相关
const enableGm = ref(true);
const sm2PrivateKey = ref("");
const showGmHelp = ref(false);

let sm2Lib = sm.sm2;
let sm4Lib = sm.sm4;

const randomSm4Key = () => {
  const chars =
    "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  let s = "";
  for (let i = 0; i < 16; i++)
    s += chars[Math.floor(Math.random() * chars.length)];
  return s;
};
const convertUtf82HEX = (utf8: string) => {
  const array = new Uint8Array(utf8.length);
  let hexString: string = "";
  for (let i = 0; i < array.length; i++) {
    hexString += `0${array[i].toString(16)}`.slice(-2);
  }
  return hexString;
};

// Base64校验（标准Base64）
const isBase64 = (s: unknown) => {
  const str = String(s ?? "");
  if (!str || str.length % 4 !== 0) return false;
  if (!/^[A-Za-z0-9+/=]+$/.test(str)) return false;
  try {
    atob(str);
    return true;
  } catch {
    return false;
  }
};

const base64ToBytes = (b64: string) => {
  const s = String(b64 || "").replace(/\s+/g, "");
  const pad = s.length % 4 ? "=".repeat(4 - (s.length % 4)) : "";
  const raw = atob(s + pad);
  return new Uint8Array(Array.from(raw).map((c) => c.charCodeAt(0)));
};
const bytesToHex = (arr: Uint8Array) =>
  Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
const hexToBytes = (hex: string) =>
  new Uint8Array(
    (hex.match(/.{1,2}/g) || []).map((b) => parseInt(b, 16))
  );
const hexToBase64 = (hex: string) => {
  const bytes = hexToBytes(hex);
  return btoa(String.fromCharCode(...Array.from(bytes)));
};
const readDerLength = (buf: Uint8Array, pos: number) => {
  let len = buf[pos];
  pos += 1;
  if ((len & 0x80) !== 0) {
    const n = len & 0x7f;
    let v = 0;
    for (let i = 0; i < n; i++) v = (v << 8) | buf[pos + i];
    pos += n;
    len = v;
  }
  return { len, pos };
};
const spkiBase64ToUncompressedHex = (b64: string) => {
  const outer = base64ToBytes(b64);
  if (outer[0] !== 0x30) throw new Error("公钥DER解析失败: 非SEQUENCE");
  const { len, pos } = readDerLength(outer, 1);
  const content = outer.subarray(pos, pos + len);
  let p = 0;
  if (content[p] !== 0x30) throw new Error("公钥DER解析失败: 算法标识缺失");
  p += 1;
  const r1 = readDerLength(content, p);
  p = r1.pos + r1.len;
  if (content[p] !== 0x03) throw new Error("公钥DER解析失败: 未找到BIT STRING");
  p += 1;
  const r2 = readDerLength(content, p);
  const bitStart = r2.pos;
  const bitLen = r2.len;
  const unused = content[bitStart];
  if (unused !== 0x00) throw new Error("公钥DER解析失败: 非零unused bits");
  const point = content.subarray(bitStart + 1, bitStart + bitLen);
  if (point.length !== 65 || point[0] !== 0x04) throw new Error("公钥格式错误: 需非压缩点");
  return bytesToHex(point);
};
const pkcs8Base64ToPrivateKeyHex = (b64: string) => {
  const buf = base64ToBytes(b64);
  let p = 0;
  if (buf[p++] !== 0x30) throw new Error("私钥DER解析失败: 非SEQUENCE");
  let r = readDerLength(buf, p);
  const end = r.pos + r.len;
  p = r.pos;
  if (buf[p++] !== 0x02) throw new Error("私钥DER解析失败: 缺少版本INTEGER");
  r = readDerLength(buf, p);
  p = r.pos + r.len;
  if (buf[p++] !== 0x30) throw new Error("私钥DER解析失败: 缺少算法SEQUENCE");
  r = readDerLength(buf, p);
  p = r.pos + r.len;
  if (buf[p++] !== 0x04) throw new Error("私钥DER解析失败: 未找到OCTET STRING");
  r = readDerLength(buf, p);
  const ecStart = r.pos;
  const ecLen = r.len;
  const ec = buf.subarray(ecStart, ecStart + ecLen);
  let q = 0;
  if (ec[q++] !== 0x30) throw new Error("私钥DER解析失败: 非ECPrivateKey SEQUENCE");
  let r2 = readDerLength(ec, q);
  const ecEnd = r2.pos + r2.len;
  q = r2.pos;
  if (ec[q++] !== 0x02) throw new Error("私钥DER解析失败: 缺少EC版本INTEGER");
  r2 = readDerLength(ec, q);
  q = r2.pos + r2.len;
  if (ec[q++] !== 0x04) throw new Error("私钥DER解析失败: 未找到ECPrivateKey OCTET STRING");
  r2 = readDerLength(ec, q);
  const privStart = r2.pos;
  const privLen = r2.len;
  let priv = ec.subarray(privStart, privStart + privLen);
  if (priv.length > 32) priv = priv.slice(priv.length - 32);
  if (priv.length < 32) priv = new Uint8Array([...new Array(32 - priv.length).fill(0), ...Array.from(priv)]);
  return bytesToHex(priv as Uint8Array);
};
const normalizeSm2PublicHex = (s: string) => {
  if (/^[A-Za-z0-9+/=]+$/.test(s)) return spkiBase64ToUncompressedHex(s);
  return s;
};
const normalizeSm2PrivateHex = (s: string) => {
  if (/^[A-Za-z0-9+/=]+$/.test(s)) return pkcs8Base64ToPrivateKeyHex(s);
  return s;
};

// 获取应用信息
const getApplicationInfo = async () => {
  try {
    const response = await httpService.get<any>(
      "/nxb/console/application/get"
    );
    if (response) {
      return {
        appId: response.appId,
        appSecret: response.appSecret,
        xencioPublicKey: response.xencioPublicKey,
      };
    }
    return null;
  } catch (error) {
    console.log("获取应用信息失败:", error);
    return null;
  }
};

// 获取客户信息
const getCustomerInfo = async () => {
  try {
    const response = await httpService.get<any>(
      "/nxb/console/customer/listWithSummary",
      {
        pageNow: 1,
        pageSize: 10,
      }
    );
    if (response && response.customers && response.customers.length > 0) {
      return response.customers[0].customerCode;
    }
    return null;
  } catch (error) {
    console.log("获取客户信息失败:", error);
    return null;
  }
};

// 登录后自动获取用户信息
const autoFetchUserInfo = async () => {
  const userInfoStr = localStorage.getItem("userInfo");
  if (userInfoStr) {
    const appInfo = await getApplicationInfo();
    if (appInfo) {
      appId.value = appInfo.appId;
      appSecret.value = appInfo.appSecret;
      xencioPublicKey.value = appInfo.xencioPublicKey;
    }
    const customerCodeValue = await getCustomerInfo();
    if (customerCodeValue) {
      customerCode.value = customerCodeValue;
    }
  }
};

onMounted(() => {
  void autoFetchUserInfo().finally(() => {
    formState.value = normalizeThirdPartyPayFormState(
      JSON.parse(JSON.stringify(formState.value)) as Record<string, unknown>
    );
  });
});

watch(
  [
    appId,
    appSecret,
    customerCode,
    formState,
    metaJson,
    activeLanguage,
    enableGm,
    response,
  ],
  () => highlightCode(),
  { flush: "post", deep: true }
);

// 语言选项
const languages = [
  { id: "curl", name: "cURL" },
  { id: "javascript", name: "JavaScript" },
  { id: "python", name: "Python" },
  { id: "java", name: "Java" },
  { id: "csharp", name: "C#" },
  { id: "php", name: "PHP" },
  { id: "ruby", name: "Ruby" },
];

const displayAppSecretForSample = () =>
  appSecret.value && appSecret.value !== DEFAULT_APP_SECRET
    ? appSecret.value
    : "8201e508ef948b8fcdca855729e7ee34";

const deepOmitEmptyStrings = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) return undefined;
  if (typeof obj === "string") return obj.trim() === "" ? undefined : obj;
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) {
    const arr = (obj as unknown[])
      .map(deepOmitEmptyStrings)
      .filter((x) => x !== undefined);
    return arr.length ? arr : undefined;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const p = deepOmitEmptyStrings(v);
    if (p === undefined) continue;
    if (
      typeof p === "object" &&
      p !== null &&
      !Array.isArray(p) &&
      Object.keys(p as object).length === 0
    )
      continue;
    out[k] = p;
  }
  return Object.keys(out).length ? out : undefined;
};

const buildRequestPayloadForDisplay = (): Record<string, unknown> => {
  const normalized = normalizeThirdPartyPayFormState(
    JSON.parse(JSON.stringify(formState.value)) as Record<string, unknown>
  );
  const base = deepOmitEmptyStrings(normalized);
  const payload =
    typeof base === "object" && base !== null
      ? { ...(base as Record<string, unknown>) }
      : {};
  const t = metaJson.value.trim();
  if (t) {
    payload.metaJson = t;
  }
  return payload;
};

const buildRequestPayload = (): Record<string, unknown> => {
  const normalized = normalizeThirdPartyPayFormState(
    JSON.parse(JSON.stringify(formState.value)) as Record<string, unknown>
  );
  const pruned = deepOmitEmptyStrings(normalized);
  if (!pruned || typeof pruned !== "object") {
    throw new Error("请先填写必填字段");
  }
  const payload = { ...(pruned as Record<string, unknown>) };
  const t = metaJson.value.trim();
  if (t) {
    payload.metaJson = t;
  }
  return payload;
};

const getRequestText = () => {
  const payload = buildRequestPayloadForDisplay();
  const payloadJson = JSON.stringify(payload, null, 2);
  const displayAppId = appId.value || "4d9414e89bc24b0d89b678d9f20bc56a";
  const displayCustomerCode = customerCode.value || "CUSTFA48DD8EFF437956";
  const displayAppSecret = displayAppSecretForSample();

  switch (activeLanguage.value) {
    case "curl":
      return `curl -X POST '${serverUrl}/api/v2/payment/thirdPay' \\\n  -H 'x-app-id: ${displayAppId}' \\\n  -H 'x-app-secret: ${displayAppSecret}' \\\n  -H 'x-customer-code: ${displayCustomerCode}' \\\n  -H 'Content-Type: application/json' \\\n  -d '${JSON.stringify(payload)}'`;

    case "javascript":
      return `const axios = require('axios');\n\nconst data = ${payloadJson};\n\nconst config = {\n  method: 'post',\n  url: '${serverUrl}/api/v2/payment/thirdPay',\n  headers: {\n    'x-app-id': '${displayAppId}',\n    'x-app-secret': '${displayAppSecret}',\n    'x-customer-code': '${displayCustomerCode}',\n    'Content-Type': 'application/json'\n  },\n  data: data\n};\n\naxios(config)\n  .then(function (response) {\n    console.log(JSON.stringify(response.data));\n  })\n  .catch(function (error) {\n    console.log(error);\n  });`;

    case "python":
      return `import requests\nimport json\n\nurl = "${serverUrl}/api/v2/payment/thirdPay"\n\npayload = ${payloadJson}\n\nheaders = {\n  'x-app-id': '${displayAppId}',\n  'x-app-secret': '${displayAppSecret}',\n  'x-customer-code': '${displayCustomerCode}',\n  'Content-Type': 'application/json'\n}\n\nresponse = requests.post(url, headers=headers, json=payload)\nprint(response.text)`;

    case "java":
      return `OkHttpClient client = new OkHttpClient();\n\nString json = ${JSON.stringify(JSON.stringify(payload))};\nRequestBody body = RequestBody.create(json, MediaType.parse("application/json"));\nRequest request = new Request.Builder()\n    .url("${serverUrl}/api/v2/payment/thirdPay")\n    .post(body)\n    .addHeader("x-app-id", "${displayAppId}")\n    .addHeader("x-app-secret", "${displayAppSecret}")\n    .addHeader("x-customer-code", "${displayCustomerCode}")\n    .build();\nResponse response = client.newCall(request).execute();\nSystem.out.println(response.body().string());`;

    case "csharp":
      return `var client = new RestClient("${serverUrl}/api/v2/payment/thirdPay");\nvar request = new RestRequest(Method.POST);\nrequest.AddHeader("x-app-id", "${displayAppId}");\nrequest.AddHeader("x-app-secret", "${displayAppSecret}");\nrequest.AddHeader("x-customer-code", "${displayCustomerCode}");\nrequest.AddHeader("Content-Type", "application/json");\nrequest.AddParameter("application/json", ${JSON.stringify(JSON.stringify(payload))}, ParameterType.RequestBody);\nIRestResponse response = client.Execute(request);\nConsole.WriteLine(response.Content);`;

    case "php":
      return `<?php\n\n$curl = curl_init();\n\ncurl_setopt_array($curl, array(\n    CURLOPT_URL => '${serverUrl}/api/v2/payment/thirdPay',\n    CURLOPT_RETURNTRANSFER => true,\n    CURLOPT_CUSTOMREQUEST => 'POST',\n    CURLOPT_POSTFIELDS => ${JSON.stringify(JSON.stringify(payload))},\n    CURLOPT_HTTPHEADER => array(\n        'x-app-id: ${displayAppId}',\n        'x-app-secret: ${displayAppSecret}',\n        'x-customer-code: ${displayCustomerCode}',\n        'Content-Type: application/json'\n    ),\n));\n\n$response = curl_exec($curl);\ncurl_close($curl);\necho $response;`;

    case "ruby":
      return `require "uri"\nrequire "net/http"\nrequire "json"\n\nurl = URI("${serverUrl}/api/v2/payment/thirdPay")\nhttps = Net::HTTP.new(url.host, url.port)\nhttps.use_ssl = true\nrequest = Net::HTTP::Post.new(url)\nrequest["x-app-id"] = "${displayAppId}"\nrequest["x-app-secret"] = "${displayAppSecret}"\nrequest["x-customer-code"] = "${displayCustomerCode}"\nrequest["Content-Type"] = "application/json"\nrequest.body = ${JSON.stringify(JSON.stringify(payload))}\nresponse = https.request(request)\nputs response.body`;

    default:
      return "";
  }
};

// 国密加密示例
const getGmSampleText = () => {
  const payload = buildRequestPayloadForDisplay();
  const displayAppId = appId.value || "4d9414e89bc24b0d89b678d9f20bc56a";
  const displayCustomerCode = customerCode.value || "CUSTFA48DD8EFF437956";
  const displayAppSecret = displayAppSecretForSample();

  switch (activeLanguage.value) {
    case "curl":
      return `# 国密加密请求示例（SM2/SM3/SM4）
# 步骤：1.生成随机SM4密钥 → 2.SM4加密业务JSON → 3.SM3摘要 → 4.SM2签名 → 5.SM2加密SM4密钥
curl -X POST '${serverUrl}/api/v2/payment/thirdPay' \\
  -H 'x-app-id: ${displayAppId}' \\
  -H 'x-app-secret: ${displayAppSecret}' \\
  -H 'x-customer-code: ${displayCustomerCode}' \\
  -H 'xencio-key: <SM2加密后的SM4密钥>' \\
  -H 'Content-Type: application/json' \\
  -d '{"reqData":"<SM4密文>","sign":"<SM2签名>"}'`;

    case "javascript":
      return `// 国密加密请求示例（SM2/SM3/SM4）
// 使用 sm-crypto 库: npm install sm-crypto
const sm = require('sm-crypto');
const axios = require('axios');

const payload = ${JSON.stringify(payload, null, 2)};
const rawJson = JSON.stringify(payload);

// 1. 生成随机SM4密钥（16字节字符串）
const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
let sm4Key = '';
for (let i = 0; i < 16; i++) sm4Key += chars[Math.floor(Math.random() * chars.length)];

// 2. SM4加密（需将sm4Key转hex）
const sm4KeyHex = Array.from(new TextEncoder().encode(sm4Key))
  .map(b => b.toString(16).padStart(2, '0')).join('');
const reqData = sm.sm4.encrypt(rawJson, sm4KeyHex);

// 3. SM3摘要
const signDigest = sm.sm3(rawJson);

// 4. SM2签名（私钥支持Base64(DER)或HEX）
const sign = sm.sm2.doSignature(signDigest, sm2PrivateKeyHex, { hash: false });

// 5. SM2加密SM4密钥
const xencioKey = sm.sm2.doEncrypt(sm4Key, xencioPublicKeyHex, 1);

// 发送请求
axios({
  method: 'post',
  url: '${serverUrl}/api/v2/payment/thirdPay',
  headers: {
    'x-app-id': '${displayAppId}',
    'x-app-secret': '${displayAppSecret}',
    'x-customer-code': '${displayCustomerCode}',
    'xencio-key': xencioKey,
    'Content-Type': 'application/json'
  },
  data: { reqData, sign }
}).then(res => console.log(JSON.stringify(res.data))).catch(err => console.log(err));`;

    case "python":
      return `# 国密加密请求示例（SM2/SM3/SM4）
# 使用 gmssl 库: pip install gmssl
import json, random, string, requests
from gmssl import sm2, sm3, sm4

# 业务数据
payload = ${JSON.stringify(payload, null, 2)}
raw_json = json.dumps(payload, ensure_ascii=False)

# 1. 生成随机SM4密钥（16字节）
sm4_key = ''.join(random.choices(string.ascii_letters + string.digits, k=16))

# 2. SM4加密（ECB模式，PKCS#7填充）
sm4_crypt = sm4.CryptSM4()
sm4_crypt.set_key(sm4_key.encode(), sm4.SM4_ENCRYPT)
req_data = sm4_crypt.crypt_ecb(raw_json.encode()).hex()

# 3. SM3摘要
sign_digest = sm3.sm3_hash(raw_json.encode())

# 4. SM2签名
sm2_crypt = sm2.CryptSM2(private_key=sm2_private_hex, public_key=xencio_public_hex)
sign = sm2_crypt.sign(sign_digest)

# 5. SM2加密SM4密钥
xencio_key = sm2_crypt.encrypt(sm4_key.encode())

# 发送请求
response = requests.post(
    '${serverUrl}/api/v2/payment/thirdPay',
    headers={
        'x-app-id': '${displayAppId}',
        'x-app-secret': '${displayAppSecret}',
        'x-customer-code': '${displayCustomerCode}',
        'xencio-key': xencio_key,
        'Content-Type': 'application/json'
    },
    json={'reqData': req_data, 'sign': sign}
)
print(response.text)`;

    case "java":
      return `// 国密加密请求示例（SM2/SM3/SM4）
// 使用 BouncyCastle (bcprov-jdk15on) + 国密扩展
import org.bouncycastle.jce.provider.BouncyCastleProvider;
import javax.crypto.Cipher;
import javax.crypto.spec.SecretKeySpec;
import java.security.*;
import java.nio.charset.StandardCharsets;

// 业务数据
String payload = ${JSON.stringify(JSON.stringify(payload))};

// 1. 生成随机SM4密钥（16字节）
SecureRandom random = new SecureRandom();
byte[] sm4Key = new byte[16];
random.nextBytes(sm4Key);

// 2. SM4加密（ECB模式，PKCS#7填充）
Security.addProvider(new BouncyCastleProvider());
Cipher cipher = Cipher.getInstance("SM4/ECB/PKCS7Padding", "BC");
cipher.init(Cipher.ENCRYPT_MODE, new SecretKeySpec(sm4Key, "SM4"));
String reqData = Hex.toHexString(cipher.doFinal(payload.getBytes(StandardCharsets.UTF_8)));

// 3. SM3摘要
MessageDigest sm3 = MessageDigest.getInstance("SM3", "BC");
byte[] digest = sm3.digest(payload.getBytes(StandardCharsets.UTF_8));

// 4. SM2签名
String sign = SM2.sign(digest, sm2PrivateKey);

// 5. SM2加密SM4密钥
String xencioKey = SM2.encrypt(sm4Key, xencioPublicKey);

// 发送请求
OkHttpClient client = new OkHttpClient();
String json = String.format("{\\"reqData\\":\\"%s\\",\\"sign\\":\\"%s\\"}", reqData, sign);
RequestBody body = RequestBody.create(json, MediaType.parse("application/json"));
Request request = new Request.Builder()
    .url("${serverUrl}/api/v2/payment/thirdPay")
    .post(body)
    .addHeader("x-app-id", "${displayAppId}")
    .addHeader("x-app-secret", "${displayAppSecret}")
    .addHeader("x-customer-code", "${displayCustomerCode}")
    .addHeader("xencio-key", xencioKey)
    .build();
Response response = client.newCall(request).execute();
System.out.println(response.body().string());`;

    case "csharp":
      return `// 国密加密请求示例（SM2/SM3/SM4）
// 使用 Savorboard.GmSm 或其他国密库
using System.Security.Cryptography;
using System.Text;
using RestSharp;

// 业务数据
var payload = ${JSON.stringify(JSON.stringify(payload))};

// 1. 生成随机SM4密钥（16字节）
var sm4Key = new byte[16];
RandomNumberGenerator.Fill(sm4Key);

// 2. SM4加密（ECB模式，PKCS#7填充）
var sm4 = new SM4CryptoServiceProvider { Key = sm4Key, Mode = CipherMode.ECB, Padding = PaddingMode.PKCS7 };
var plainBytes = Encoding.UTF8.GetBytes(payload);
var encrypted = sm4.CreateEncryptor().TransformFinalBlock(plainBytes, 0, plainBytes.Length);
var reqData = Convert.ToHexString(encrypted).ToLower();

// 3. SM3摘要
var sm3 = new SM3CryptoServiceProvider();
var digest = sm3.ComputeHash(plainBytes);

// 4. SM2签名
var sm2 = new SM2CryptoServiceProvider();
sm2.ImportParameters(sm2PrivateParameters);
var sign = sm2.SignData(digest);

// 5. SM2加密SM4密钥（需将sm4Key转字符串）
var xencioKey = sm2.Encrypt(Encoding.UTF8.GetString(sm4Key));

// 发送请求
var client = new RestClient("${serverUrl}/api/v2/payment/thirdPay");
var request = new RestRequest(Method.POST);
request.AddHeader("x-app-id", "${displayAppId}");
request.AddHeader("x-app-secret", "${displayAppSecret}");
request.AddHeader("x-customer-code", "${displayCustomerCode}");
request.AddHeader("xencio-key", xencioKey);
request.AddHeader("Content-Type", "application/json");
request.AddJsonBody(new { reqData, sign });
IRestResponse response = client.Execute(request);
Console.WriteLine(response.Content);`;

    case "php":
      return `<?php
// 国密加密请求示例（SM2/SM3/SM4）
// 使用 lpilp/guomi 或其他国密库: composer require lpilp/guomi
require_once 'vendor/autoload.php';

// 业务数据
$payload = ${JSON.stringify(JSON.stringify(payload))};

// 1. 生成随机SM4密钥（16字节）
$sm4Key = bin2hex(random_bytes(16));

// 2. SM4加密
$sm4 = new \\Gm\\SM4($sm4Key);
$reqData = bin2hex($sm4->encrypt($payload));

// 3. SM3摘要
$sm3 = new \\Gm\\SM3();
$signDigest = $sm3->hash($payload);

// 4. SM2签名
$sm2 = new \\Gm\\SM2($sm2PrivateKey, $xencioPublicKey);
$sign = $sm2->sign($signDigest);

// 5. SM2加密SM4密钥
$xencioKey = $sm2->encrypt($sm4Key);

// 发送请求
$curl = curl_init();
curl_setopt_array($curl, array(
    CURLOPT_URL => '${serverUrl}/api/v2/payment/thirdPay',
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_CUSTOMREQUEST => 'POST',
    CURLOPT_POSTFIELDS => json_encode(array(
        'reqData' => $reqData,
        'sign' => $sign
    )),
    CURLOPT_HTTPHEADER => array(
        'x-app-id: ${displayAppId}',
        'x-app-secret: ${displayAppSecret}',
        'x-customer-code: ${displayCustomerCode}',
        'xencio-key: ' . $xencioKey,
        'Content-Type: application/json'
    ),
));
$response = curl_exec($curl);
curl_close($curl);
echo $response;`;

    case "ruby":
      return `# 国密加密请求示例（SM2/SM3/SM4）
# 使用本地国密库或OpenSSL 3.0+国密扩展
require "uri"
require "net/http"
require "json"
require "securerandom"

# 业务数据
payload = ${JSON.stringify(JSON.stringify(payload))}

# 1. 生成随机SM4密钥（16字节）
sm4_key = SecureRandom.alphanumeric(16)

# 2. SM4加密（调用国密扩展或本地服务）
req_data = sm4_encrypt(payload, sm4_key)

# 3. SM3摘要
sign_digest = sm3_hash(payload)

# 4. SM2签名
sign = sm2_sign(sign_digest, sm2_private_key_hex)

# 5. SM2加密SM4密钥
xencio_key = sm2_encrypt(sm4_key, xencio_public_key_hex)

# 发送请求
url = URI("${serverUrl}/api/v2/payment/thirdPay")
https = Net::HTTP.new(url.host, url.port)
https.use_ssl = true
request = Net::HTTP::Post.new(url)
request["x-app-id"] = "${displayAppId}"
request["x-app-secret"] = "${displayAppSecret}"
request["x-customer-code"] = "${displayCustomerCode}"
request["xencio-key"] = xencio_key
request["Content-Type"] = "application/json"
request.body = { reqData: req_data, sign: sign }.to_json
response = https.request(request)
puts response.body`;

    default:
      return "";
  }
};

// ── 必填字段（lodash 路径，与对外 API 一致） ──
const requiredPaths = [
  "businessTransNo",
  "payAmount",
  "payCurrency",
  "fromAccountInfo.fromAccountNo",
  "toAccountInfo.toAccountNo",
];

const isPathEmpty = (path: string) => {
  const v = lodashGet(formState.value, path);
  return v === undefined || v === null || String(v).trim() === "";
};

const submitPayment = async () => {
  showValidation.value = true;

  if (!appId.value?.trim() || !appSecret.value?.trim() || !customerCode.value?.trim()) {
    alert("请填写 x-app-id、x-app-secret、x-customer-code");
    return;
  }

  const missing = requiredPaths.filter(isPathEmpty);
  if (missing.length) {
    alert(`请填写必填字段：${missing.join("、")}`);
    return;
  }


  let payloadObj: Record<string, unknown>;
  try {
    payloadObj = buildRequestPayload();
  } catch (err) {
    alert((err as Error).message);
    return;
  }

  if (enableGm.value) {
    if (!xencioPublicKey.value?.trim() || !sm2PrivateKey.value?.trim()) {
      alert("请填写平台SM2公钥与企业SM2私钥");
      return;
    }
  }

  loading.value = true;
  response.value = "";

  try {
    const headers: Record<string, string> = {
      "x-app-id": appId.value,
      "x-app-secret": appSecret.value,
      "x-customer-code": customerCode.value,
      "Content-Type": "application/json",
    };
    let bodyToSend: any = null;

    if (enableGm.value) {
      const rawJson = JSON.stringify(payloadObj);
      const sm4Key = randomSm4Key();
      const sm3Lib = sm.sm3;
      const sm4KeyHex = convertUtf82HEX(sm4Key);

      const reqData = sm4Lib.encrypt(rawJson, sm4KeyHex);
      const signDigest = sm3Lib(rawJson);
      const sm2PrivHex = normalizeSm2PrivateHex(sm2PrivateKey.value);
      const xencioPubHex = normalizeSm2PublicHex(xencioPublicKey.value);
      const sign = sm2Lib.doSignature(signDigest, sm2PrivHex, { hash: false });
      const xencioKeyHeader = sm2Lib.doEncrypt(sm4Key, xencioPubHex, 1);

      headers["xencio-key"] = xencioKeyHeader;
      bodyToSend = { reqData, sign };
    } else {
      bodyToSend = payloadObj;
    }

    const res = await fetch(`${serverUrl}/api/v2/payment/thirdPay`, {
      method: "POST",
      headers,
      body: JSON.stringify(bodyToSend),
    });

    const text = await res.text();

    if (enableGm.value) {
      let obj: any;
      try { obj = JSON.parse(text); } catch { response.value = text; return; }

      if (obj && obj.respData) {
        const encKeyHeader = res.headers.get("xencio-key") || res.headers.get("XENCIO-KEY");
        if (!encKeyHeader) {
          response.value = JSON.stringify({ error: "响应缺少xencio-key头" }, null, 2);
          return;
        }
        const respSm4Key = sm2Lib.doDecrypt(encKeyHeader, normalizeSm2PrivateHex(sm2PrivateKey.value), 1);
        const respSm4KeyHex = convertUtf82HEX(respSm4Key);
        const originResp = sm4Lib.decrypt(obj.respData, respSm4KeyHex);
        try {
          response.value = JSON.stringify({ originResp: JSON.parse(originResp) }, null, 2);
        } catch {
          response.value = JSON.stringify({ originResp }, null, 2);
        }
      } else {
        response.value = text;
      }
    } else {
      try {
        response.value = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        response.value = text;
      }
    }
  } catch (error) {
    response.value = JSON.stringify({ error: "请求失败: " + (error as Error).message }, null, 2);
  } finally {
    loading.value = false;
  }
};

</script>
